'use es6';

import tinymce from 'tinymce';
import PortalIdParser from 'PortalIdParser';
import { SupportedHubLVideoParams } from 'FileManagerLib/Constants';
import Routes from 'ContentUtils/Routes';
import { omit } from 'rich-text-lib/utils/iterables';
import { VideoSizes } from 'rich-text-lib/constants/video';
import { Alignments } from 'rich-text-lib/constants/alignments';
import { getPluginSetting } from '../utils';
import { VIDEO_SETTINGS, hsvideoConfig, HublVideoTypes } from '../constants/hsvideo';
import { resizeAssetDimensions, getMaxDimensions, getAlignment, getMargins, retainAttributesAndInnerHtml, toDimensionAttribute } from '../utils/shared';
import { getFetchVideo2ById } from 'tinymce-data/api/Video';
import { trackInteraction } from '../utils/usageTracking';
export const hublVideoPattern = new RegExp(/{%\s+video_player\s+("[a-zA-Z0-9,_]+"|'[a-zA-Z0-9,_]+').*?\s+player_id=("(?:[a-zA-Z0-9]+)".*?|'(?:[a-zA-Z0-9]+)'.*?)\s+%}/);
export const attribPrefix = 'data-hs-video-';
export const mceAttribPrefix = `data-mce-p-${attribPrefix}`;
const DefaultVideoDimensions = {
  WIDTH: 800,
  HEIGHT: 450
};
export function getVideoPluginSetting(editor, setting) {
  const embedPluginSettings = getPluginSetting(editor, VIDEO_SETTINGS);
  if (embedPluginSettings) {
    return embedPluginSettings[setting];
  }
  return null;
}
export const convertAttrToParam = attr => {
  if (!isNaN(parseInt(attr, 10)) && parseInt(attr, 10).toString() === attr) {
    return parseInt(attr, 10);
  } else if (attr === 'true' || attr === 'false') {
    return attr === 'true';
  }
  return attr;
};
export const VideoNodeAttributes = {
  COLOR: `data-mce-p-${attribPrefix}color`,
  PLAY_BUTTON_COLOR: `data-mce-p-${attribPrefix}play_button_color`,
  CONVERSION_ASSET: `data-mce-p-${attribPrefix}conversion_asset`
};
const getPlayerColor = (editor, node) => {
  const playerColor = editor.dom.getAttrib(node, VideoNodeAttributes.COLOR);
  return playerColor ? `#${playerColor}` : null;
};
const cleanDimensionStyles = cssText => {
  const cssObject = tinymce.DOM.parseStyle(cssText);
  const stylesToRemove = ['height', 'max-height', 'overflow'];
  if (!cssObject.float && cssObject.display !== 'inline-block') {
    // Remove style properties if video is centered (default)
    stylesToRemove.push('width', 'max-width', 'margin-left', 'margin-right', 'display');
  }
  stylesToRemove.forEach(property => {
    delete cssObject[property];
  });
  return tinymce.DOM.serializeStyle(cssObject);
};
const parseConversionAsset = str => {
  if (typeof str !== 'string') {
    return null;
  }
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
};
export const serializeConversionAsset = obj => {
  if (!obj) {
    return '';
  }
  return JSON.stringify(obj);
};
const parseHublParamValue = (param, value) => {
  switch (param) {
    case 'type':
      if (value === HublVideoTypes.HSVIDEO2) {
        return value;
      }
      // Auto-convert iframe-style HubSpot video players to `scriptV4`
      return HublVideoTypes.VIDYARD;
    case 'conversion_asset':
      return parseConversionAsset(value);
    case 'style':
      return tinymce.DOM.parseStyle(value);
    case 'width':
    case 'height':
    case 'player_id':
      return parseInt(value, 10);
    case 'embed_button':
    case 'viral_sharing':
    case 'hide_playlist':
    case 'overrideable':
    case 'autoplay':
    case 'hidden_controls':
    case 'loop':
    case 'muted':
    case 'full_width':
      return value === 'True';
    case 'color':
    case 'playlist_color':
    case 'play_button_color':
    default:
      return value;
  }
};
export const getVideoParamsFromHtml = (node, prefix = attribPrefix) => {
  const videoParams = {};
  SupportedHubLVideoParams.forEach(key => {
    const keyAttrib = node.getAttribute(`${prefix}${key}`);
    switch (key) {
      case 'conversion_asset':
        videoParams[key] = parseConversionAsset(keyAttrib);
        break;
      case 'style':
        videoParams[key] = cleanDimensionStyles(node.getAttribute(`${key}`));
        break;
      case 'width':
      case 'height':
        videoParams[key] = convertAttrToParam(keyAttrib);
        break;
      default:
        if (keyAttrib !== null) {
          videoParams[key] = convertAttrToParam(keyAttrib);
        }
    }
  });
  return videoParams;
};
export const serializeVideoParamValue = (param, value) => {
  switch (param) {
    case 'conversion_asset':
      return serializeConversionAsset(value);
    case 'style':
      return tinymce.DOM.serializeStyle(value);
    default:
      return `${value}`;
  }
};
const V2_UNSUPPORTED_PARAMS = ['hide_playlist', 'viral_sharing', 'embed_button', 'full_width'];
const V2_BOOLEAN_PARAMS = ['autoplay', 'hidden_controls', 'loop', 'muted'];
export const getIframeUrl = video => {
  const {
    player_id: playerId
  } = video;
  const portalId = PortalIdParser.get();
  const query = {
    portalId,
    full_width: true // Always use full_width for the preview for optimal responsiveness
  };
  Object.keys(omit(['player_id', 'style', 'type', 'overrideable', 'full_width'], video)).forEach(param => {
    query[param] = serializeVideoParamValue(param, video[param]);
  });
  const params = Object.assign({}, omit(V2_UNSUPPORTED_PARAMS.concat(V2_BOOLEAN_PARAMS), query), {
    playerType: 'richTextPreview',
    renderContext: 'rich-text-preview'
  });
  V2_BOOLEAN_PARAMS.forEach(param => {
    if (video[param]) {
      params[param] = true;
    }
  });
  return Routes.getVideo2PreviewUrl(playerId, params, {
    usePublicDomain: true
  });
};
export const parseHublVideoTag = hubl => {
  const result = {};
  SupportedHubLVideoParams.forEach(param => {
    const regex = new RegExp(`\\s${param}=(".*?"|'.*?'|True|False)(?:,|\\s)`);
    let value = regex.exec(hubl);
    if (value !== null && value.length === 2) {
      value = value[1].trim();
      if (value.startsWith("'") || value.startsWith('"')) {
        // Strip leading and trailing quotes
        value = value.slice(1, -1);
      }
      if (value.length > 0) {
        result[param] = parseHublParamValue(param, value);
      }
    }
  });
  return result;
};
export const possiblyResizeVideoDimensions = (editor, video) => {
  const maxContainerWidth = getVideoPluginSetting(editor, hsvideoConfig.MAX_DIMENSION_WIDTH);
  const {
    width,
    height
  } = resizeAssetDimensions(maxContainerWidth, video.width || DefaultVideoDimensions.WIDTH, video.height || DefaultVideoDimensions.HEIGHT);
  return Object.assign({}, video, {
    width,
    height
  });
};
export const getVideoAttributes = (editor, node) => {
  const maxContainerWidth = getVideoPluginSetting(editor, hsvideoConfig.MAX_DIMENSION_WIDTH);
  const {
    dom
  } = editor;
  const width = parseInt(dom.getAttrib(node, `${mceAttribPrefix}width`), 10);
  const height = parseInt(dom.getAttrib(node, `${mceAttribPrefix}height`), 10);
  const isFullWidth = dom.getAttrib(node, `${mceAttribPrefix}full_width`) === 'true';
  return {
    alignment: isFullWidth ? Alignments.JUSTIFY : getAlignment(node),
    dimensions: {
      width,
      height
    },
    maxDimensions: getMaxDimensions(maxContainerWidth, width, height),
    margin: getMargins(editor, node),
    playerColor: getPlayerColor(editor, node),
    sizeOption: isFullWidth ? VideoSizes.FULL : VideoSizes.CUSTOM,
    showDimensionsControls: !isFullWidth
  };
};
export const getFetchVideoAttributes = editor => {
  const {
    apiClient
  } = editor.settings;
  const fetchVideo2ById = getFetchVideo2ById(apiClient);
  return playerId => {
    return new Promise(resolve => {
      fetchVideo2ById(playerId).then(data => resolve(Object.assign({}, data, {
        name: data.title
      }))).catch(error => {
        console.error(error);
        resolve({});
      });
    });
  };
};

/*
 * Hubspot Video (Vidyard) Preview Node
 */
export function createHublVideoPreview(editor, node) {
  const previewAttributes = {
    contentEditable: 'false',
    'data-mce-hubl-video': 'true',
    class: `mce-preview-object${node.attr('class') ? ` ${node.attr('class')}` : ''}`
  };
  const dimensionStyles = {};
  const innerWrapperDimensionStyles = {};
  const width = node.attr(`${attribPrefix}width`);
  const height = node.attr(`${attribPrefix}height`);
  const fullWidth = node.attr(`${attribPrefix}full_width`);
  const enableFullWidth = getVideoPluginSetting(editor, hsvideoConfig.ENABLE_FULL_WIDTH);
  if (!enableFullWidth || fullWidth !== 'true') {
    // Only set max width & height when not full width
    if (width) {
      dimensionStyles['max-width'] = toDimensionAttribute(width);
    }
    if (height) {
      dimensionStyles['max-height'] = toDimensionAttribute(height);
    }
  }
  const previewWrapper = new tinymce.html.Node('div', 1);
  const nodeStyles = editor.dom.parseStyle(node.attr('style'));
  if (nodeStyles.margin) {
    dimensionStyles.margin = nodeStyles.margin;
  } else {
    dimensionStyles['margin-top'] = nodeStyles['margin-top'] || 0;
    dimensionStyles['margin-bottom'] = nodeStyles['margin-bottom'] || 0;
    dimensionStyles['margin-left'] = nodeStyles['margin-left'] || 'auto';
    dimensionStyles['margin-right'] = nodeStyles['margin-right'] || 'auto';
  }
  dimensionStyles.width = '100%';
  dimensionStyles.height = 'auto';
  dimensionStyles.overflow = 'hidden';
  dimensionStyles.display = nodeStyles.display || 'block';
  const previewInnerWrapper = new tinymce.html.Node('div', 1);
  innerWrapperDimensionStyles['max-width'] = '100%';
  if (height && width) {
    innerWrapperDimensionStyles['padding-bottom'] = `${toDimensionAttribute(height, false) / toDimensionAttribute(width, false) * 100}%`;
  }
  innerWrapperDimensionStyles.height = 0;
  innerWrapperDimensionStyles.overflow = 'hidden';
  innerWrapperDimensionStyles.position = 'relative';
  previewInnerWrapper.attr({
    style: editor.dom.serializeStyle(Object.assign({}, innerWrapperDimensionStyles))
  });
  previewWrapper.append(previewInnerWrapper);
  previewAttributes.style = editor.dom.serializeStyle(Object.assign({}, nodeStyles, dimensionStyles));
  previewWrapper.attr(previewAttributes);
  retainAttributesAndInnerHtml(editor, node, previewWrapper);
  const previewNode = new tinymce.html.Node('iframe', 1);
  previewNode.attr({
    src: node.attr('src'),
    allowfullscreen: node.attr('allowfullscreen'),
    width: '100%',
    height: '100%',
    frameborder: '0',
    controls: node.attr('controls')
  });
  const shimNode = new tinymce.html.Node('span', 1);
  shimNode.attr('class', 'mce-shim');
  previewNode.attr({
    style: 'position: absolute; top: 0px; left: 0px; border: none;'
  });
  previewInnerWrapper.append(previewNode);
  previewWrapper.append(shimNode);
  return previewWrapper;
}
export function extractIframeFromHublVideoPreview(editor, node) {
  const iframeEl = new tinymce.html.Node('iframe', 1);
  const style = node.attr('style');
  if (style) {
    iframeEl.attr({
      style
    });
  }

  // Unprefix all placeholder attributes
  node.attributes.forEach(({
    name,
    value
  }) => {
    if (name.indexOf('data-mce-p-') === 0) {
      iframeEl.attr(name.substr(11), value);
    }
  });
  return iframeEl;
}
export const retainAttributesOfReplacedVideo = (node, newWidth, newHeight) => {
  const videoParams = getVideoParamsFromHtml(node, mceAttribPrefix);
  const aspectRatio = newHeight / newWidth;
  const width = Math.min(videoParams.width, newWidth);
  const height = Math.round(aspectRatio * width);
  return Object.assign({}, omit(['player_id', 'type'], videoParams), {
    width,
    height
  });
};
export const getTrackVideoInteraction = editor => {
  return action => trackInteraction(editor, 'tinymceVideo', action, {
    type: 'video'
  });
};